<template>
  <div class="event-preview">
    <div class="event-content">
      <div class="event-frame">
        <style v-if="blockAdvance" type="text/css">
          .hero.is-slide .direction { display: none; }
        </style>
        <EventPage
          @event:update="setEvent"
          v-if="shortcode"
          :code="shortcode"
          :blockAdvance="blockAdvance.block"
          :blockAdvanceMessage="blockAdvance.message"
          :isPreview="true"
        />
      </div>

      <div class="event-progress">
        <b-steps v-model="previewStep" :has-navigation="false" :mobile-mode="null" icon-pack="fas" type="is-primary">
          <b-step-item label="Join &amp; Share" icon="user-circle" />
          <b-step-item label="Interact" icon="arrows-alt-h" />
          <b-step-item label="Soundboard" icon="volume-up" />
          <b-step-item label="Word Cloud" icon="comments" />
        </b-steps>
      </div>
    </div>
    <div class="help-content">
      <transition name="fade">
        <div v-if="players.length === 0">
          <p>When you launch an event it starts on the join slide — you'll share this screen with your audience on a video call or projector.</p>
          <p>Players join with a QR code or link using their phone or computer</p>
          <p class="next-action">
            📲 Join as a player! Take out your phone, scan the QR code, and follow the prompts
          </p>
          <span class="m-l-5">
            <b-tooltip
              label="No phone or having trouble? Try opening a new page in your web browser and go to the URL shown in the middle box of the join screen"
              position="is-left"
              type="is-white"
              size="is-large"
              multilined
            >
              <b-icon pack="fas" icon="question-circle" />
            </b-tooltip>
          </span>
        </div>
        <div v-else-if="players.length < 5">
          <style>
            .step-item.is-active + .step-item:before {
            background-position: 66% bottom !important;
            }
          </style>
          <p>Great! Now let's add some demo players.</p>
          <p style="margin-bottom: 0px">How many people are normally in your events?</p>
          <FeedbackButtons
            :questionId="3"
            :options="eventSizes"
            :includeOther="false"
            :showTitle="false"
            :buttonClass="'is-primary'"
            @next="addTestPlayers"
          />
        </div>
        <div v-else-if="players.length > 5 && slide === 1">
          <style>
            .step-item.is-active + .step-item:before {
            background-position: 33% bottom !important;
            }
          </style>
          <p>It’s a party!🎉</p>
          <p>As the host, you control the pace of the event, just like a slide show.</p>
          <!-- <p>As the host, you control the pace of the event (just like a slide show). Player phones will automatically sync position.</p> -->
          <!-- <h2 class="title next-action-title">Advance the Slide</h2> -->
          <p class="next-action">👈 Advance the slide by clicking the right side of the presentation</p>
        </div>
        <div v-else-if="players.length > 5 && slide === 2 && step === 1">
          <template v-if="responses.length < 1">
            <p>Bingo! Now everyone's on the new slide with you.</p>
            <p>&larr; The main "host" display screen looks like this, and your audience can send answers with their phones.</p>
            <p class="next-action">📲 Tap an option on your phone to send your response 😉</p>
          </template>
          <template v-else>
            <style>
              .step-item.is-active + .step-item:before {
              background-position: 66% bottom !important;
              }
            </style>
            <p>Great! As our bots send in their ratings you'll see their icons fill in along the bottom of the slide. This is how you'll know how many people have answered.</p>
            <p>✅ And now it looks like everyone has responded!</p>
            <p class="next-action">👈 Advance the presentation from the host screen to see the results</p>
          </template>
        </div>
        <div v-else-if="players.length > 5 && slide === 2 && step === 2">
          <style>
            .step-item.is-active + .step-item:before {
            background-position: 33% bottom !important;
            }
          </style>
          <p>The results are in &mdash; our bots are in a good mood! 🙌</p>
          <p class="next-action">👈 Advance to the next slide when you're ready</p>
        </div>
        <div v-else-if="players.length > 5 && slide === 3">
          <template v-if="reactions.length < 1">
            <p>The soundboard is a great way to liven up an event — groups love it!</p>
            <p>If it becomes too much you can always mute it from the bottom right of the host screen.</p>
            <p class="next-action">📲 Tap the 🔊 button on the bottom right of your phone and pick a sound to send</p>
          </template>
          <template v-else>
            <style>
              .step-item.is-active + .step-item:before {
              background-position: 50% bottom !important;
              }
            </style>
            <p>Music to our ears! 🎶</p>
            <p class="next-action">👈 Advance to the next slide for our last demo: an interactive word cloud!</p>
          </template>
        </div>
        <div v-else-if="players.length > 5 && slide === 4 && step === 1">
          <p>This is a live-updating word cloud, one of our many interactive slides!</p>
          <p><span class="next-action">📲 Type a word or two on your phone and hit send</span></p>
          <p v-if="responses.length > 0" class="next-action">👈 Advance the slide when you're ready</p>
        </div>
        <div v-else-if="players.length > 5 && slide === 4 && step === 2">
          <p>It's word art! 🎨</p>
          <p>You can send in more answers and vote for other answers that you like — the word cloud will update in real time.</p>
          <p><span class="next-action">📲 Vote for another player's answer by tapping it on your phone</span></p>
          <p v-if="responses.length > 1" class="next-action">👈 Advance the slide when you're finished!</p>
        </div>
        <div v-else-if="players.length > 5 && slide === 5">
          <p>Now you've learned to host an event on Slides With Friends!</p>
          <p>Did you know every part of this is customizeable? You can modify text, add or remove interactions, and even build entirely custom events.</p>
          <b-button @click="endEventFromLink()" type="is-primary">Make your first deck &rarr;</b-button>
        </div>
      </transition>
    </div>
    <button @click="startExitModal()" type="button" class="modal-close is-large" />
  </div>
</template>
<script>
import { saveEventSettings } from '~/queries/saveEventState.gql'
import { playerList } from '~/common/mocks/players.js'
import FeedbackButtons from '~/components/FeedbackButtons'
import EventControls from '~/components/mixins/EventControls'
import EndEvent from '~/components/mixins/EndEvent'
import EventPage from '~/pages/event/_id'
import { demoDeckId } from '~/common/decks'

export default {
  name: 'EventPreview',
  components: {
    EventPage,
    FeedbackButtons
  },
  mixins: [
    EventControls,
    EndEvent
  ],
  props: {
    deckId: { default: false, required: true },
    shortcode: { default: false, required: true }
  },
  data () {
    return {
      isPreviewEvent: true,
      event: {
        id: '',
        participants: [],
        responses: {},
        startTime: '',
        primaryUser: '',
        position: {},
        reactions: [],
        chat: {}
      },
      targetPlayerNumber: 0,
      eventSizes: [
        '< 10',
        '10-30',
        '30-50',
        '50-100',
        '100-250',
        '> 250'
      ],
      eventSizeMap: {
        '< 10': 8,
        '10-30': 20,
        '30-50': 40,
        '50-100': 80,
        '100-250': 150,
        '> 250': 250
      }
    }
  },
  computed: {
    players () {
      return this.event.participants || []
    },
    slide () {
      return this.event.position.currentSlide || 1
    },
    previewStep () {
      return this.slide - 1
    },
    step () {
      return this.event.position.step || 1
    },
    responses () {
      if (!this.event.responses) { return [] }
      return this.event.responses.filter(r => r.slidePosition === this.slide)
    },
    reactions () {
      if (!this.event.reactions) { return [] }
      return this.event.reactions.filter(r => r.slideNumber === this.slide)
    },
    blockAdvance () {
      if (this.slide === 1 && this.players.length < 1) {
        return { block: true, message: 'Join on your phone or computer before advancing 😉' }
      }

      if (this.slide === 1 && this.players.length < 5) {
        return { block: true, message: 'Select a group size before advancing 👪' }
      }

      if (this.slide === 2 && this.responses.length < 1) {
        return { block: true, message: 'Select a rating from your phone to move forward 😉' }
      }

      if (this.slide === 4 && this.step === 1 && this.responses.length < 1) {
        return { block: true, message: 'Send a word from your phone to continue 😉' }
      }

      return { block: false }
    }
  },
  watch: {
    players () {
      if (this.players.length === 1 && this.deckId === demoDeckId) {
        this.posthogCapture('onboard:demo_player_join', {
          event_id: this.event.id,
          shortcode: this.shortcode,
          player_id: this.players[0].id,
          host_id: this.event.primaryUser
        })
      }
    }
  },
  async mounted () {
  },
  methods: {
    setEvent (newEvent) {
      this.log('preview event updated')
      this.event = newEvent
      if (newEvent.position && this.event.position.currentSlide === 5) {
        this.saveOnboarding('onboard-demo-finished')
        this.posthogCapture('onboard:demo_complete', {
          event_id: this.event.id,
          shortcode: this.shortcode,
          host_id: this.event.primaryUser
        })
      }
    },
    addTestPlayers (val) {
      const num = this.eventSizeMap[val]
      this.targetPlayerNumber = num
      const players = playerList.slice(0, num)

      console.log('adding ' + num + ' test players ', players)

      this.saveEventSetting('testPlayers', 1)

      setTimeout(() => {
        this.saveEventSetting('testPlayers', 2)
      }, 500)

      setTimeout(() => {
        this.saveEventSetting('testPlayers', this.targetPlayerNumber)
      }, 1100)
    },
    saveEventSetting (setting, value) {
      const settings = Object.assign({}, this.event.settings)
      settings[setting] = value
      this.event.settings[setting] = value

      console.log('saving event settings: ', settings)

      this.$apollo.mutate({
        mutation: saveEventSettings,
        variables: {
          eventId: this.event.id,
          settings
        }
      })
    },
    startExitModal () {
      if (this.slide === 5) {
        this.endEventFromLink()
        return
      }
      this.$buefy.dialog.confirm({
        // title: 'End this event?',
        message: '<p class="m-t-10"><b>Are you sure you want to leave this demo? Any progress will be lost.</b></p>',
        cancelText: 'Cancel',
        class: 'delete-deck',
        confirmText: `Exit Live Demo`,
        onConfirm: () => {
          this.endEvent(this.event.id)
          this.$emit('close', { event: this.event })
        }
      })
    },
    endEventFromLink () {
      this.endEvent(this.event.id)
      this.$emit('close', { showCompletedToast: true })
    }
  }
}
</script>
<style lang="scss">
.event-preview-modal.modal {
  justify-content: center;
  padding: 2rem 1.5rem 2rem 2rem;

  .modal-background {
    background-color: rgba(20,20,20, .95);
  }

  .modal-content {
    width: 100%;
    height: 100%;
    max-width: none !important;
    max-height: none !important;
    margin:0;
  }
  .event-preview {
    width: 100%;
    height: 100%;
    max-height: calc(100vh - 40px);
    display: flex;
    align-items: center;
    top: -1.5em;
    position: relative;
  }

  .event-progress {
    position: absolute;
    top: 104%;
    width: 95%;
    left: 3%;
    color:#ccc;

    .b-steps .steps {
      min-height: 2em;
      font-size: calc(.7vh + .7vw);
      opacity: .9;
    }

    .b-steps .steps .step-items .step-item {

      &:not(:first-child)::before {
        top: .45em;
        height: .25em;
      }

      &.is-active {
        .step-link {
          color: var(--primary);
          color: white;
          font-weight: bold;
        }
        .step-marker {
          background-color: var(--primary);
          border: .2em solid #fff;
        }

        .step-title {
          font-weight: bold;
        }
      }

        &.is-previous {
        .step-link {
          color: #666;
        }
        .step-marker {
          background-color: var(--primary);
          border: .2em solid var(--primary);
        }

      }

      .step-marker {
        background-color: #aaa;
        font-size: 1.25em;
        width: 1em;
        height: 1em;
        border: .2em solid #ddd;

        .icon {
          display: none;
          font-size: 1em;
          width: 1em;
          height: 1em;

          * {
            font-size: 1em;

            &:before {
              font-size: 1em;
            }
          }
        }
      }
      .step-link {
        color: #aaa;
      }

      .step-details {
        padding-top: .2em;
      }

      .step-title {
        font-size: 1.2em;
        line-height: 1.2em;
      }
    }
  }

  .event-content {
    // background-color: white;
    width: 65%;
    height: 70%;
    padding: 2rem 2.5rem;
    position: relative;

    .event-frame {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 0%; right: 0%;
      margin: 0 auto;
      border-radius: 12px;
      overflow: hidden;
    }
  }

  .help-content {
    max-width: 30%;
    margin: 2rem 0rem 2rem 2.2rem;
    color: white;
    font-size: calc(1.2vh + 1.2vw);
    line-height: 1.25em;

    p {
      margin-bottom: 1.75em;
    }

    .button {
      font-size: calc(.9vh + .9vw);
      font-weight: bold;
      border-width: 2px;
    }

    .title.next-action {
      color: #aaa;
      font-size: .65em;
      text-transform: uppercase;
      letter-spacing: .5px;
      margin-bottom: .5em;
      text-decoration: none;
    }

    .next-action-box {
      // border: 3px solid rgba(226,7,97,.8);
      background-color: rgba(226,7,97,.8);
      border-radius: 12px;
      padding: .35em;
      margin: 0 0 0 -.35em;
    }

    .title.next-action-title {
      text-transform: uppercase;
      background-color: black;
      color: #bbb;
      font-size: .55em;
      letter-spacing: .5px;
      padding: .5em .75em;
      margin-bottom: .5em;
      border-radius: 6px;
    }

    .next-action {
      // text-decoration: underline;
      // text-decoration-color: rgba(226,7,97,.8);
      // text-decoration-thickness: 3px;
      display: inline;
      clear: both;
      line-height: 1.5em;
      font-weight: 500;
      background-color: rgba(226,7,97,.8);
      // padding: 0 .1em;
      // background-color: black;
      // padding: .75em;
      // margin-left: -.75em;
      // padding: .5em;
      // border-radius: 12px;

      // &.title {
      //   font-size: 1.25em;
      //   text-decoration-thickness: 5px;
      //   line-height: 1em;
      //   color: white;
      //   margin-bottom: .5em;
      // }

      &.link {
      text-decoration: underline;
      text-decoration-color: rgba(226,7,97,.8);
      text-decoration-thickness: 3px;
      background-color: transparent;
      cursor: pointer;
      }
    }
  }

  .b-tooltip.is-multiline .tooltip-content {
    line-height: 1.45em;
    padding: 1.2em;
    font-size: .65em;
    width: 20em;
    border-radius: 10px;
    box-shadow: 2px 5px 14px -4px rgba(0,0,0,.6);

  }
}

</style>
